import React, { useEffect, useState } from 'react';
import api from '../../api/axios';
import { getToken } from '../../utils/auth';
import { toast, ToastContainer } from 'react-toastify';
import { format, parseISO } from 'date-fns';
import 'react-toastify/dist/ReactToastify.css';

const DiasEspecialesAdmin = () => {
  const [excepciones, setExcepciones] = useState({});
  const [mostrarModal, setMostrarModal] = useState(false);
  const [nuevaExcepcion, setNuevaExcepcion] = useState({
    fecha: '',
    cerrado: false,
    hora_inicio: '',
    hora_fin: ''
  });

  const cargarExcepciones = async () => {
    const token = getToken();
    try {
      const res = await api.get('/excepciones', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExcepciones(res.data.excepciones || {});
    } catch (err) {
      console.error('❌ Error al cargar excepciones:', err);
      toast.error('Error al cargar las excepciones');
    }
  };

  useEffect(() => {
    cargarExcepciones();
  }, []);

  const crearExcepcion = async () => {
    const token = getToken();
    const { fecha, cerrado, hora_inicio, hora_fin } = nuevaExcepcion;

    if (!fecha) {
      toast.warning('Selecciona una fecha válida');
      return;
    }

    if (!cerrado && (!hora_inicio || !hora_fin)) {
      toast.warning('Debes indicar la hora de inicio y fin');
      return;
    }

    try {
      const payload = cerrado
        ? { fecha, cerrado: true }
        : {
            fecha,
            tramos: [{ hora_inicio: `${hora_inicio}:00`, hora_fin: `${hora_fin}:00` }]
          };

      await api.post('/excepcion', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Excepción creada correctamente');
      setNuevaExcepcion({ fecha: '', cerrado: false, hora_inicio: '', hora_fin: '' });
      setMostrarModal(false);
      cargarExcepciones();
    } catch (err) {
      console.error('❌ Error al crear excepción:', err);
      const mensaje = err.response?.data?.message || 'Error al crear la excepción';
      toast.error(mensaje);
    }
  };

  const eliminarExcepcion = async (id) => {
    if (!window.confirm('¿Seguro que deseas eliminar esta excepción?')) return;

    const token = getToken();
    try {
      await api.delete(`/excepcion/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Excepción eliminada');
      cargarExcepciones();
    } catch (err) {
      console.error('❌ Error al eliminar excepción:', err);
      toast.error('Error al eliminar la excepción');
    }
  };

  const duplicarExcepcion = (tramo, fechaOriginal) => {
    const cerrado = tramo.activo === false;
    const duplicada = {
      fecha: '',
      cerrado,
      hora_inicio: cerrado ? '' : tramo.hora_inicio.slice(0, 5),
      hora_fin: cerrado ? '' : tramo.hora_fin.slice(0, 5)
    };
    setNuevaExcepcion(duplicada);
    setMostrarModal(true);
    toast.info('Configuración copiada. Elige una nueva fecha.');
  };

  const formatearHora = (horaStr) => {
    const [h, m] = horaStr.split(':');
    return `${h.padStart(2, '0')}:${m}`;
  };

  const formatearFecha = (fechaStr) => {
    const fecha = parseISO(fechaStr);
    const opciones = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return fecha.toLocaleDateString('es-ES', opciones);
  };

  const excepcionesOrdenadas = Object.entries(excepciones).sort(([fechaA], [fechaB]) => {
    return new Date(fechaA) - new Date(fechaB);
  });

  return (
    <main className="site-width">
      <div className="texto-dashboard">
        <h1>Días Especiales</h1>
        <p>Gestiona cierres totales y horarios especiales que sobrescriben tu horario habitual.</p>
      </div>

      <div className="header-dias-especiales">
        <button 
          className="boton-añadir-excepcion"
          onClick={() => {
            setNuevaExcepcion({ fecha: '', cerrado: false, hora_inicio: '', hora_fin: '' });
            setMostrarModal(true);
          }}
        >
          + Añadir día especial
        </button>
      </div>

      {/* Lista de excepciones */}
      <div className="lista-excepciones">
        {excepcionesOrdenadas.length > 0 ? (
          excepcionesOrdenadas.map(([fecha, tramos]) => {
            const cerrado = tramos.some(t => t.activo === false);
            const fechaObj = parseISO(fecha);
            const esHoy = format(new Date(), 'yyyy-MM-dd') === fecha;
            const esPasado = fechaObj < new Date() && !esHoy;

            return (
              <div key={fecha} className={`excepcion-card ${cerrado ? 'cerrado' : 'especial'} ${esPasado ? 'pasado' : ''}`}>
                <div className="excepcion-fecha">
                  <div className="fecha-numero">{format(fechaObj, 'dd')}</div>
                  <div className="fecha-mes">{format(fechaObj, 'MMM', { locale: { localize: { month: (n) => ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'][n] } } })}</div>
                  <div className="fecha-año">{format(fechaObj, 'yyyy')}</div>
                </div>

                <div className="excepcion-contenido">
                  <div className="excepcion-info">
                    <h3>{formatearFecha(fecha)}</h3>
                    <div className={`tipo-badge ${cerrado ? 'cerrado-badge' : 'especial-badge'}`}>
                      {cerrado ? 'Cerrado' : 'Horario especial'}
                    </div>
                    {!cerrado && (
                      <div className="horarios-lista">
                        {tramos.map((t, idx) => (
                          <div key={idx} className="horario-item">
                            {formatearHora(t.hora_inicio)} - {formatearHora(t.hora_fin)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="excepcion-acciones">
                    <button
                      className="boton-accion-duplicar"
                      onClick={() => duplicarExcepcion(tramos[0], fecha)}
                      title="Duplicar"
                    >
                      <img src="/images/duplicar.svg" alt="Duplicar" />
                    </button>
                    <button
                      className="boton-accion-eliminar"
                      onClick={() => eliminarExcepcion(tramos[0].id)}
                      title="Eliminar"
                    >
                      <img src="/images/cancelar.svg" alt="Eliminar" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="sin-excepciones">
            <p>No hay días especiales configurados</p>
            <p className="texto-secundario">Añade cierres o horarios especiales para fechas específicas</p>
          </div>
        )}
      </div>

      {/* Modal para crear/editar excepción */}
      {mostrarModal && (
        <div className="modal-nuevo-horario" onClick={() => setMostrarModal(false)}>
          <div className="modal-contenido" onClick={(e) => e.stopPropagation()}>
            <h3>Nuevo día especial</h3>
            <div className="form-nuevo-horario">
              <div className="campo-horario">
                <label>Fecha</label>
                <input
                  type="date"
                  value={nuevaExcepcion.fecha}
                  onChange={(e) => setNuevaExcepcion({ ...nuevaExcepcion, fecha: e.target.value })}
                  min={format(new Date(), 'yyyy-MM-dd')}
                />
              </div>

              <div className="campo-horario">
                <label>Tipo</label>
                <select
                  value={nuevaExcepcion.cerrado ? '1' : '0'}
                  onChange={(e) => setNuevaExcepcion({ ...nuevaExcepcion, cerrado: e.target.value === '1' })}
                >
                  <option value="0">Horario especial</option>
                  <option value="1">Cerrado todo el día</option>
                </select>
              </div>

              {!nuevaExcepcion.cerrado && (
                <>
                  <div className="campo-horario">
                    <label>Hora inicio</label>
                    <input
                      type="time"
                      value={nuevaExcepcion.hora_inicio}
                      onChange={(e) => setNuevaExcepcion({ ...nuevaExcepcion, hora_inicio: e.target.value })}
                    />
                  </div>

                  <div className="campo-horario">
                    <label>Hora fin</label>
                    <input
                      type="time"
                      value={nuevaExcepcion.hora_fin}
                      onChange={(e) => setNuevaExcepcion({ ...nuevaExcepcion, hora_fin: e.target.value })}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="acciones-modal">
              <button className="boton-crear" onClick={crearExcepcion}>
                Crear
              </button>
              <button 
                className="boton-cancelar-modal" 
                onClick={() => {
                  setMostrarModal(false);
                  setNuevaExcepcion({ fecha: '', cerrado: false, hora_inicio: '', hora_fin: '' });
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="bottom-right" autoClose={3000} />
    </main>
  );
};

export default DiasEspecialesAdmin;
